import React, { useState, useEffect } from "react";
import ConfirmDeleteModal from "./confirmation";

const Modal = ({ isOpen, closeModal, header, additionalData, id, deleteUri }) => {
  const overlayStyle = isOpen ? "block" : "hidden";
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAppDeleteConfirmation, setShowAppDeleteConfirmation] = useState(false);
  const [currentAppIndex, setCurrentAppIndex] = useState(0);

  // Reset index when modal opens with new data
  useEffect(() => {
    setCurrentAppIndex(0);
  }, [additionalData]);

  const applications = Array.isArray(additionalData) ? additionalData : additionalData ? [additionalData] : [];
  const currentApp = applications[currentAppIndex];
  const totalApps = applications.length;

  const nextApp = () => {
    setCurrentAppIndex((prev) => (prev + 1) % totalApps);
  };

  const prevApp = () => {
    setCurrentAppIndex((prev) => (prev - 1 + totalApps) % totalApps);
  };

  const deleteCurrentApp = async () => {
    if (!currentApp?._appId) return;
    try {
      const response = await fetch('/form/delete-application', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationId: currentApp._appId })
      });
      if (response.ok) {
        setShowAppDeleteConfirmation(false);
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to delete application:', error);
    }
  };

  return (
    <div className={`fixed inset-0 z-50 overflow-auto ${overlayStyle}`}>
      <div className="flex items-center justify-center min-h-screen">
        <div className="modal-overlay absolute w-full h-full bg-gray-900 opacity-50"></div>
        <div className="modal-container bg-white rounded-lg shadow-lg z-50 w-full sm:max-w-lg lg:max-w-2xl">
          <div className="modal-content p-6">
            <div className="modal-header flex justify-between items-center border-b pb-4">
              <h3 className="modal-title text-lg font-semibold text-gray-800">Lead Details</h3>
              {/* Close button */}
              <button className="modal-close text-red-500 text-2xl focus:outline-none" onClick={closeModal}>
                &times;
              </button>
            </div>
            <div className="modal-body text-gray-700 max-h-96 overflow-y-auto">
              {/* Base information */}
              <div className="mb-6">
                <p className="font-semibold mb-2 text-lg">Base Information</p>
                {header && Object.entries(header).map(([key, value]) => (
                  <div key={key}>
                    <p className="text-sm mb-1">
                      <span className="font-semibold">{key.replace(/_/g, " ").toUpperCase()}: </span>
                      {value}
                    </p>
                  </div>
                ))}
              </div>
              {/* Separator */}
              <hr className="my-6 border-t border-gray-300" />
              {/* Application Navigation */}
              {totalApps > 0 && showDetails && (
                <div className="flex items-center justify-between mb-4">
                  <p className="font-semibold text-lg">
                    Application {currentAppIndex + 1} of {totalApps}
                    {currentApp?.Duplicate === "Yes" && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500 text-white">
                        Duplicate
                      </span>
                    )}
                  </p>
                  {totalApps > 1 && (
                    <div className="flex gap-2">
                      <button
                        onClick={prevApp}
                        className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                      >
                        ← Previous
                      </button>
                      <button
                        onClick={nextApp}
                        className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>
              )}
              {/* Additional fields */}
              {currentApp && showDetails && (
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {Object.entries(currentApp).map(([key, value]) => (
                      key !== 'A→B Application File' && key !== 'B→C Application File' && key !== 'Duplicate' && (
                        <div key={key}>
                          <p className="text-sm font-semibold">{key}:</p>
                          <p className="text-sm">{value}</p>
                        </div>
                      )
                    ))}
                  </div>
                  {/* Download buttons */}
                  <div className="mt-4 flex gap-4">
                    {currentApp['A→B Application File'] && (
                      <a
                        href={currentApp['A→B Application File']}
                        className="text-blue-500 hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download A→B Application
                      </a>
                    )}
                    {currentApp['B→C Application File'] && (
                      <a
                        href={currentApp['B→C Application File']}
                        className="text-blue-500 hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download B→C Application
                      </a>
                    )}
                  </div>
                  {/* Duplicate Note */}
                  {currentApp['Duplicate Note'] && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-sm text-yellow-800">{currentApp['Duplicate Note']}</p>
                    </div>
                  )}
                  {/* Delete individual application (only when multiple exist) */}
                  {totalApps > 1 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      {!showAppDeleteConfirmation ? (
                        <button
                          onClick={() => setShowAppDeleteConfirmation(true)}
                          className="text-sm text-red-500 hover:text-red-700"
                        >
                          Delete this application only
                        </button>
                      ) : (
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-600">Are you sure?</span>
                          <button
                            onClick={deleteCurrentApp}
                            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                          >
                            Yes, delete
                          </button>
                          <button
                            onClick={() => setShowAppDeleteConfirmation(false)}
                            className="px-3 py-1 bg-gray-200 rounded text-sm hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
         
          {totalApps > 0 && (
            <div className="modal-footer bg-gray-100 py-3 px-6 space-x-2 flex justify-end">
              <button
                className="text-blue-500 font-semibold hover:text-blue-700"
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? "Hide Details" : `Show Details (${totalApps} application${totalApps > 1 ? 's' : ''})`}
              </button>
            </div>
          )}
          <div className="modal-footer bg-gray-100 py-3 px-6 space-x-2 flex justify-end">
             <button
              className="text-red-500 font-semibold hover:text-red-700"
              onClick={() => setShowDeleteConfirmation((prev) => !prev)}
            >
              {totalApps > 1 ? 'Delete Lead & All Applications' : 'Delete Lead'}
          </button>
          </div>
         
        </div>
      </div>
      <ConfirmDeleteModal isOpen={showDeleteConfirmation} closeModal={() => setShowDeleteConfirmation(prev => !prev)} itemId={id} uri={deleteUri}/>
    </div>
  );
};

export default Modal;
